/**
 * DomainConfigPage — Domain Rules + Plan Config CRUD
 *
 * Reads/writes platform_settings via:
 *   GET    /api/v1/domain-configs?prefix=...   — list keys
 *   GET    /api/v1/domain-configs/:key         — read value
 *   PUT    /api/v1/domain-configs/:key         — upsert value
 *   DELETE /api/v1/domain-configs/:key         — delete key
 *   POST   /api/v1/domain-configs/cache/clear  — hot-reload (clear in-memory cache)
 *
 * Key naming convention:
 *   rules:domain:{domain}
 *   rules:tenant:{tenantId}:{domain}
 *   plan:domain:{domain}:{planId}
 *   plan:tenant:{tenantId}:{planId}
 */

import { useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  RefreshCw,
  Loader2,
  Search,
  Settings2,
  Pencil,
  Trash2,
  Plus,
  Zap,
  AlertTriangle,
  CheckCircle2,
} from 'lucide-react';

// ─── Types ──────────────────────────────────────────────────────────────────

interface ConfigRow {
  key: string;
  updated_at: string;
}

interface ConfigEntry {
  key: string;
  value: unknown;
  updated_at: string;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function keyTypeLabel(key: string): { label: string; variant: 'default' | 'secondary' | 'outline' } {
  if (key.startsWith('rules:domain:')) return { label: 'domain rule', variant: 'default' };
  if (key.startsWith('rules:tenant:')) return { label: 'tenant rule', variant: 'secondary' };
  if (key.startsWith('plan:domain:')) return { label: 'domain plan', variant: 'default' };
  if (key.startsWith('plan:tenant:')) return { label: 'tenant plan', variant: 'secondary' };
  return { label: 'config', variant: 'outline' };
}

// ─── Component ──────────────────────────────────────────────────────────────

export default function DomainConfigPage() {
  const [rows, setRows] = useState<ConfigRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ type: 'ok' | 'err'; msg: string } | null>(null);

  // Edit dialog
  const [editing, setEditing] = useState<ConfigEntry | null>(null);
  const [editJson, setEditJson] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // New key dialog
  const [newOpen, setNewOpen] = useState(false);
  const [newKey, setNewKey] = useState('');
  const [newJson, setNewJson] = useState('{\n  \n}');
  const [newJsonError, setNewJsonError] = useState<string | null>(null);

  const showToast = (type: 'ok' | 'err', msg: string) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 3000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get<ConfigRow[]>('/domain-configs');
      setRows(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const filtered = rows.filter((r) =>
    !search || r.key.toLowerCase().includes(search.toLowerCase()),
  );

  // ── Open editor ──
  const openEdit = async (key: string) => {
    try {
      const cfg = await api.get<ConfigEntry>(`/domain-configs/${encodeURIComponent(key)}`);
      setEditing(cfg);
      setEditJson(JSON.stringify(cfg.value, null, 2));
      setJsonError(null);
    } catch {
      showToast('err', 'Failed to load config');
    }
  };

  // ── Save ──
  const save = async () => {
    if (!editing) return;
    let parsed: unknown;
    try {
      parsed = JSON.parse(editJson);
      setJsonError(null);
    } catch (e) {
      setJsonError(e instanceof Error ? e.message : 'Invalid JSON');
      return;
    }
    setSaving(true);
    try {
      await api.put(`/domain-configs/${encodeURIComponent(editing.key)}`, parsed);
      showToast('ok', `Saved: ${editing.key}`);
      setEditing(null);
      void load();
    } catch (e) {
      showToast('err', e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ──
  const deleteKey = async (key: string) => {
    if (!confirm(`Delete config key:\n${key}`)) return;
    try {
      await api.delete(`/domain-configs/${encodeURIComponent(key)}`);
      showToast('ok', `Deleted: ${key}`);
      void load();
    } catch (e) {
      showToast('err', e instanceof Error ? e.message : 'Delete failed');
    }
  };

  // ── Create new ──
  const createNew = async () => {
    if (!newKey.trim()) { setNewJsonError('Key is required'); return; }
    let parsed: unknown;
    try {
      parsed = JSON.parse(newJson);
      setNewJsonError(null);
    } catch (e) {
      setNewJsonError(e instanceof Error ? e.message : 'Invalid JSON');
      return;
    }
    setSaving(true);
    try {
      await api.put(`/domain-configs/${encodeURIComponent(newKey.trim())}`, parsed);
      showToast('ok', `Created: ${newKey}`);
      setNewOpen(false);
      setNewKey('');
      setNewJson('{\n  \n}');
      void load();
    } catch (e) {
      setNewJsonError(e instanceof Error ? e.message : 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  // ── Hot reload ──
  const hotReload = async () => {
    try {
      await api.post('/domain-configs/cache/clear', {});
      showToast('ok', 'Cache cleared — rules will reload from DB on next request');
    } catch {
      showToast('err', 'Cache clear failed');
    }
  };

  return (
    <div className="p-6 space-y-5">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-2 rounded-md px-4 py-2 text-sm shadow-lg ${toast.type === 'ok' ? 'bg-emerald-600 text-white' : 'bg-destructive text-destructive-foreground'}`}>
          {toast.type === 'ok' ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Settings2 className="h-6 w-6 text-primary" />
            Domain Config
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            管理领域规则 (rules:*) 和执行计划 (plan:*) — 存储于 platform_settings，零重启热更新
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => void hotReload()}>
            <Zap className="h-4 w-4 mr-1" />
            Hot Reload
          </Button>
          <Button size="sm" variant="outline" onClick={() => void load()} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
          <Button size="sm" onClick={() => { setNewOpen(true); setNewKey(''); setNewJson('{\n  \n}'); setNewJsonError(null); }}>
            <Plus className="h-4 w-4 mr-1" />
            New Config
          </Button>
        </div>
      </div>

      {/* Info panel */}
      <div className="rounded-lg border border-blue-200 bg-blue-50/60 p-3 text-xs text-blue-800 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-300">
        <p className="font-medium mb-1">Config Key 说明</p>
        <ul className="list-disc list-inside space-y-0.5 text-blue-700 dark:text-blue-400">
          <li><code className="font-mono text-[11px]">rules:domain:*</code> — 领域级默认规则 (e.g. sea-import blocked ports)</li>
          <li><code className="font-mono text-[11px]">rules:tenant:*</code> — 租户覆盖规则 (优先于 domain 默认)</li>
          <li><code className="font-mono text-[11px]">plan:domain:*</code> — 领域级执行计划 (step plan JSON)</li>
          <li><code className="font-mono text-[11px]">plan:tenant:*</code> — 租户计划覆盖 (inject / override / remove steps)</li>
        </ul>
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-md bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          className="pl-9"
          placeholder="Filter by key (e.g. lch-logistics, sea-import, plan:domain)"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Quick filter buttons */}
      <div className="flex flex-wrap gap-2">
        {['rules:domain:', 'rules:tenant:', 'plan:domain:', 'plan:tenant:'].map((prefix) => (
          <Button
            key={prefix}
            size="sm"
            variant={search === prefix ? 'default' : 'outline'}
            className="text-xs h-7"
            onClick={() => setSearch(search === prefix ? '' : prefix)}
          >
            {prefix}
          </Button>
        ))}
      </div>

      {/* Table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-muted-foreground font-normal">
            {filtered.length} of {rows.length} entries
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading && rows.length === 0 ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center text-muted-foreground text-sm py-10">No configs found</div>
          ) : (
            <div className="divide-y divide-border">
              {filtered.map((row) => {
                const { label, variant } = keyTypeLabel(row.key);
                return (
                  <div key={row.key} className="flex items-center px-4 py-3 gap-3 hover:bg-muted/30 group">
                    <Badge variant={variant} className="text-xs shrink-0">{label}</Badge>
                    <code className="text-xs font-mono flex-1 truncate">{row.key}</code>
                    <span className="text-xs text-muted-foreground shrink-0 hidden sm:block">
                      {new Date(row.updated_at).toLocaleDateString()}
                    </span>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => void openEdit(row.key)}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 hover:text-destructive" onClick={() => void deleteKey(row.key)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit dialog */}
      {editing && (
        <Dialog open onOpenChange={(o) => { if (!o) setEditing(null); }}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-mono text-sm">{editing.key}</DialogTitle>
            </DialogHeader>
            <div className="space-y-2">
              <Textarea
                className="font-mono text-xs min-h-[400px]"
                value={editJson}
                onChange={(e) => { setEditJson(e.target.value); setJsonError(null); }}
                spellCheck={false}
              />
              {jsonError && (
                <p className="text-xs text-destructive flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />{jsonError}
                </p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              <Button onClick={() => void save()} disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* New config dialog */}
      {newOpen && (
        <Dialog open onOpenChange={(o) => { if (!o) setNewOpen(false); }}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>New Config Entry</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium mb-1 block">Key</label>
                <Input
                  className="font-mono text-xs"
                  placeholder="e.g. rules:tenant:my-tenant:sea-import"
                  value={newKey}
                  onChange={(e) => setNewKey(e.target.value)}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Formats: <code>rules:domain:&#123;domain&#125;</code> · <code>rules:tenant:&#123;tenantId&#125;:&#123;domain&#125;</code> · <code>plan:domain:&#123;domain&#125;:&#123;planId&#125;</code> · <code>plan:tenant:&#123;tenantId&#125;:&#123;planId&#125;</code>
                </p>
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block">JSON Value</label>
                <Textarea
                  className="font-mono text-xs min-h-[300px]"
                  value={newJson}
                  onChange={(e) => { setNewJson(e.target.value); setNewJsonError(null); }}
                  spellCheck={false}
                />
                {newJsonError && (
                  <p className="text-xs text-destructive flex items-center gap-1 mt-1">
                    <AlertTriangle className="h-3 w-3" />{newJsonError}
                  </p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setNewOpen(false)}>Cancel</Button>
              <Button onClick={() => void createNew()} disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Create
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
